import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ConstructionIcon } from "lucide-react";
import { FC } from "react";
import { ErrorContent } from "@/components/IAndI/SummaryMetrics/ErrorContent";
import { NotFoundContent } from "@/components/IAndI/SummaryMetrics/NotFoundContent";

const STATUS_COLORS: Record<Dryp.BlockageStatusValues, { bg: string; text: string }> = {
	Blocked: { bg: "bg-red-50", text: "text-red-700" },
	Warning: { bg: "bg-orange-50", text: "text-orange-700" },
	Normal: { bg: "bg-green-50", text: "text-green-700" },
	Full: { bg: "bg-blue-50", text: "text-blue-700" },
	NoData: { bg: "bg-gray-50", text: "text-gray-500" },
	Undetermined: { bg: "bg-gray-50", text: "text-gray-500" },
};

const BlockageStatusCardSkeleton: FC = () => (
	<div className="flex flex-col items-center gap-4">
		<Skeleton className="h-12 w-64 rounded-md bg-blue-50 mt-5" />
		<div className="flex flex-wrap gap-2 text-xs justify-center">
			<Skeleton className="h-6 w-24 rounded-md bg-blue-50" />
			<Skeleton className="h-6 w-24 rounded-md bg-blue-50" />
			<Skeleton className="h-6 w-24 rounded-md bg-blue-50" />
		</div>
	</div>
);

interface BlockageStatusCardProps {
	loading: boolean;
	loaded: boolean;
	error: ApiError | null;
	blockages: Dryp.Blockage[];
}

export const BlockageStatusCard: FC<BlockageStatusCardProps> = ({ loading, loaded, error, blockages }) => {
	const statusCounts = blockages.reduce<Record<string, number>>((acc, b) => {
		const s = b.status.status;
		acc[s] = (acc[s] || 0) + 1;
		return acc;
	}, {});

	const blockedCount = (statusCounts["Blocked"] || 0) + (statusCounts["Full"] || 0);
	const warningCount = statusCounts["Warning"] || 0;
	const normalCount = statusCounts["Normal"] || 0;
	const isNoData = loaded && !error && blockages.length === 0;

	return (
		<Card className="shadow-sm border border-muted rounded-xl flex flex-col h-full drop-shadow-sm">
			<CardHeader className="pb-2">
				<CardTitle className="text-lg font-semibold flex items-center gap-2">
					<ConstructionIcon className="h-4 w-4 text-red-500" />
					Blockage Status Overview
				</CardTitle>
			</CardHeader>

			<CardContent className="flex-1 flex flex-col justify-center">
				{loading ? (
					<BlockageStatusCardSkeleton />
				) : isNoData ? (
					<NotFoundContent message="No blockage data available" />
				) : error ? (
					<ErrorContent
						error={error}
						notFoundMessage="No blockage data available"
						message="Unable to load blockage data"
					/>
				) : (
					<>
						<div className="text-center mb-3">
							<div className="text-3xl font-bold text-primary leading-tight">{blockages.length}</div>
							<div className="text-xs text-muted-foreground mt-0.5">
								structures monitored for blockages
							</div>
						</div>

						<div className="flex flex-wrap gap-1.5 text-[11px] justify-center">
							{blockedCount > 0 && (
								<div
									className={`${STATUS_COLORS.Blocked.bg} ${STATUS_COLORS.Blocked.text} px-2 py-1 rounded-md font-medium`}
								>
									{blockedCount} blocked
								</div>
							)}
							{warningCount > 0 && (
								<div
									className={`${STATUS_COLORS.Warning.bg} ${STATUS_COLORS.Warning.text} px-2 py-1 rounded-md font-medium`}
								>
									{warningCount} warning
								</div>
							)}
							{normalCount > 0 && (
								<div
									className={`${STATUS_COLORS.Normal.bg} ${STATUS_COLORS.Normal.text} px-2 py-1 rounded-md font-medium`}
								>
									{normalCount} normal
								</div>
							)}
						</div>
					</>
				)}
			</CardContent>
		</Card>
	);
};
